/**
 * OutputMeter — horizontal peak-level meter tapped off the master bus.
 *
 * Visuals:
 *   - A filling bar whose width tracks the current peak amplitude.
 *   - A thin peak-hold line that latches the max value for ~800ms then
 *     falls back down, so transient peaks stay visible.
 *   - A small clip LED that lights whenever any sample crosses a
 *     pre-clip threshold. Stays lit for ~400ms after the last hot frame
 *     so a single blown transient doesn't flash-and-miss.
 *
 * Why not getByteFrequencyData? Frequency data is a spectrum, not an
 * amplitude. We want instantaneous peak to know how loud the master is
 * right now — that's time-domain. `getFloatTimeDomainData` gives us the
 * raw samples; the peak is just `max(|x|)` across the buffer.
 *
 * Created eagerly at app startup with a placeholder DOM. Call `attach()`
 * once the engine is unlocked to wire the analyser and start the rAF
 * loop. Before attach the bar just sits at zero.
 */

import type { AudioEngine } from '@ac-slayer/synth-core';

/** Amplitude (0..1+) above which we consider the signal clipping. */
const CLIP_THRESHOLD = 0.98;
/** How long the peak-hold line stays latched before dropping, in ms. */
const PEAK_HOLD_MS = 800;
/** How long the clip LED stays lit after the last clip frame, in ms. */
const CLIP_HOLD_MS = 400;

export class OutputMeter {
  readonly element: HTMLElement;
  private readonly fillEl: HTMLElement;
  private readonly peakEl: HTMLElement;
  private readonly clipEl: HTMLElement;

  private analyser: AnalyserNode | null = null;
  private buffer: Float32Array | null = null;
  private rafId: number | null = null;

  private heldPeak = 0;
  private heldPeakSetAt = 0;
  private lastClipAt = 0;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'output-meter';
    this.element.setAttribute('aria-label', 'Master output level');

    const track = document.createElement('div');
    track.className = 'output-meter-track';
    this.fillEl = document.createElement('div');
    this.fillEl.className = 'output-meter-fill';
    this.peakEl = document.createElement('div');
    this.peakEl.className = 'output-meter-peak';
    track.append(this.fillEl, this.peakEl);
    this.element.appendChild(track);

    this.clipEl = document.createElement('div');
    this.clipEl.className = 'output-meter-clip';
    this.clipEl.title = 'Clip indicator';
    this.element.appendChild(this.clipEl);
  }

  /**
   * Connect the meter to the engine's master bus and start the rAF loop.
   * Safe to call multiple times — repeated calls rewire the analyser so
   * the meter survives engine rebuilds.
   */
  attach(engine: AudioEngine): void {
    this.detach();
    const ctx = engine.context;
    const analyser = ctx.createAnalyser();
    // 1024 samples gives us ~21ms @ 48kHz — plenty to catch a peak
    // without eating too much main-thread time per frame.
    analyser.fftSize = 1024;
    try {
      engine.master.connect(analyser);
    } catch {
      /* master not ready yet — attach() will be called again later */
    }
    this.analyser = analyser;
    this.buffer = new Float32Array(analyser.fftSize);
    this.startLoop();
  }

  /** Stop the loop and release the analyser. Idempotent. */
  detach(): void {
    if (this.rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = null;
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        /* already disconnected */
      }
      this.analyser = null;
    }
    this.buffer = null;
    this.heldPeak = 0;
    this.lastClipAt = 0;
    this.fillEl.style.width = '0%';
    this.peakEl.style.left = '0%';
    this.clipEl.classList.remove('active');
  }

  private startLoop(): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    const tick = (): void => {
      this.drawFrame();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private drawFrame(): void {
    if (!this.analyser || !this.buffer) return;
    this.analyser.getFloatTimeDomainData(
      this.buffer as unknown as Float32Array<ArrayBuffer>,
    );
    // Amplitude peak over this frame's samples.
    let peak = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      const v = Math.abs(this.buffer[i] ?? 0);
      if (v > peak) peak = v;
    }
    const now = performance.now();

    // Hot sample latches the clip LED for a visible beat.
    if (peak >= CLIP_THRESHOLD) this.lastClipAt = now;

    // Peak-hold: latches the max, then linearly decays so the indicator
    // doesn't get "stuck" on an outlier.
    if (peak > this.heldPeak) {
      this.heldPeak = peak;
      this.heldPeakSetAt = now;
    } else {
      const sinceHold = now - this.heldPeakSetAt;
      if (sinceHold > PEAK_HOLD_MS) {
        // Drop at ~1.0 per second — feels snappy without dropping too fast.
        const decayPerMs = 1 / 1000;
        this.heldPeak = Math.max(peak, this.heldPeak - decayPerMs * 16);
      }
    }

    // Render. Clamp to 1.0 for width so hot signal doesn't overflow.
    const fillPct = Math.min(1, peak) * 100;
    const heldPct = Math.min(1, this.heldPeak) * 100;
    this.fillEl.style.width = `${fillPct.toFixed(2)}%`;
    this.peakEl.style.left = `${heldPct.toFixed(2)}%`;
    this.clipEl.classList.toggle('active', now - this.lastClipAt < CLIP_HOLD_MS);
  }

  dispose(): void {
    this.detach();
  }
}
